import type { AdItem, ChannelSummary } from '@apm/contracts';
import type { FlowerAdInfo } from '@apm/bloom-receiver-sdk';

/**
 * v38 · ONAIR CSPM — 앱의 **순수한 부분** (`docs/77` §5 · 절대 규칙 101). DOM·hls.js·Flower 를 모른다.
 *
 * 둘이다 — ① 이 채널을 CSPM 으로 틀지의 판정(`onairAdInsertionFor` · 한 함수) ② Flower 리스너의 사건을 광고 화면의 상태로
 * 접는 것(`reduceOnairAd`). 광고 화면의 근거는 **리스너와 `AdInfo.vastAdXml` 뿐**이다 — 앱은 `<video>` 의 주소도 재생목록도
 * 읽어 「광고인가」를 판정하지 않는다(이 저장소는 그 m3u8 을 보지도 고치지도 않는다 · 규칙 94·101).
 */

export type OnairAdInsertion =
  | { mode: 'cspm'; adTagUrl: string; breakSec: number }
  | { mode: 'plain'; reason: 'not-cspm' | 'native-player' | 'cspm-off' };

/**
 * **켜는 판정은 이것 하나다**(규칙 101) — 채널이 `cspm`(서버가 태그를 실었다) · `resolvePlayerKind() === 'hls'` · `?cspm=off`
 * 아님. 아이폰(네이티브 HLS)은 언제나 슬레이트다(`docs/77` §0.2 · Flower 가 재생목록을 바꿀 자리가 없다).
 */
export function onairAdInsertionFor(
  channel: Pick<ChannelSummary, 'onair'>,
  playerKind: 'hls' | 'native',
  search: string,
): OnairAdInsertion {
  const cspm = channel.onair?.cspm ?? null;
  if (cspm === null) return { mode: 'plain', reason: 'not-cspm' };
  if (new URLSearchParams(search).get('cspm') === 'off') return { mode: 'plain', reason: 'cspm-off' };
  if (playerKind !== 'hls') return { mode: 'plain', reason: 'native-player' };
  return { mode: 'cspm', adTagUrl: cspm.adTagUrl, breakSec: cspm.breakSec };
}

/**
 * 태그에 **기기 id(`dev`)를 덧붙인다** — 스킵 뒤의 재요청에는 Flower 가 앱의 `extraParams` 를 싣지 않으므로(`docs/77` §5.3)
 * 태그 자체에 있어야 광고 서버의 트래킹이 그 기기를 안다. 매크로 `[AD_DURATION_SEC]` 은 건드리지 않는다.
 */
export function cspmTagFor(adTagUrl: string, deviceId: string): string {
  return `${adTagUrl}${adTagUrl.includes('?') ? '&' : '?'}dev=${encodeURIComponent(deviceId)}`;
}

/** 광고 화면의 상태 — 브레이크 하나 동안만 산다 */
export interface OnairAdState {
  /** 브레이크 안인가(Flower 의 `onPlay` ~ `onCompleted`) */
  inBreak: boolean;
  /** `onPlay` 순간의 **재생 시계**(초 · 아래 `advancePlayClock`) — 남은 시간을 센다 */
  breakStartMediaSec: number | null;
  /**
   * bloom.24 — Flower 가 알린 **이 브레이크의 남은 길이**(초 · `onAdBreakStarted` · `docs/74` §4F). 머리에서 시작했으면 채널의 고정
   * 길이와 같고, 한가운데 합류면 남은 만큼이다 — Flower 는 그것을 `CUE-OUT-CONT` 의 `Duration − ElapsedTime` 에서 안다. `onPlay` 와
   * 따로 오므로 어느 쪽이 먼저여도 받는다. 못 받았으면 `null` 이고 시계는 머리로 센다.
   */
  announcedRemainingSec: number | null;
  /** 지금 광고 — 필러면 `filler: true` 이고 `item` 이 없어도 된다 */
  current: {
    adId: string | null;
    filler: boolean;
    /** `vastAdXml` 을 `vast-ad.ts` 로 읽은 것 · 못 읽으면 `null`(그때 광고 구매를 그리지 않는다 · 규칙 54) */
    item: AdItem | null;
    startMediaSec: number;
    skipOffsetSec: number | null;
    skip: 'none' | 'waiting' | 'armed' | 'expired';
  } | null;
  /** 이 브레이크의 광고·필러 수 · 매칭 실패 수(디버그 · 하네스) */
  counts: { ads: number; fillers: number; unmatched: number };
  /** 마지막으로 Flower 가 브레이크를 슬레이트에 맡긴 이유(`onAdBreakSkipped` · 1 광고 없음 · 2 시간 초과 · 3 오류) */
  lastSkippedReason: number | null;
}

export const ONAIR_AD_IDLE: OnairAdState = {
  inBreak: false,
  breakStartMediaSec: null,
  announcedRemainingSec: null,
  current: null,
  counts: { ads: 0, fillers: 0, unmatched: 0 },
  lastSkippedReason: null,
};

export type OnairAdEvent =
  | { type: 'breakStarted'; remainingMs: number }
  | { type: 'play'; mediaSec: number }
  | { type: 'adPlay'; info: FlowerAdInfo; mediaSec: number }
  | { type: 'skippable'; info: FlowerAdInfo }
  | { type: 'skipExpired'; info: FlowerAdInfo }
  | { type: 'completed' }
  | { type: 'breakSkipped'; reason: number };

export function reduceOnairAd(
  state: OnairAdState,
  event: OnairAdEvent,
  parseAd: (xml: string) => AdItem | null,
): OnairAdState {
  switch (event.type) {
    case 'breakStarted':
      // `onPlay` 앞이면 브레이크 밖에서 받아 둔다 — 다음 `play` 가 잇는다
      return { ...state, announcedRemainingSec: event.remainingMs / 1000 };
    case 'play':
      return {
        ...ONAIR_AD_IDLE,
        inBreak: true,
        breakStartMediaSec: event.mediaSec,
        // 브레이크 밖에서 받아 둔 것만 잇는다 — 브레이크 안의 값은 지난 브레이크의 것이다
        announcedRemainingSec: state.inBreak ? null : state.announcedRemainingSec,
        lastSkippedReason: state.lastSkippedReason,
      };
    case 'adPlay': {
      const filler = event.info.sourceType === 'FILLER';
      const xml = event.info.vastAdXml ?? '';
      const item = filler || xml === '' ? null : parseAd(xml);
      const skipOffsetSec =
        event.info.isSkippable && typeof event.info.skipOffsetMs === 'number' ? event.info.skipOffsetMs / 1000 : null;
      return {
        ...state,
        // 브레이크 시작을 못 들었어도(합류 직후) 광고가 왔으면 브레이크 안이다
        inBreak: true,
        breakStartMediaSec: state.breakStartMediaSec ?? event.mediaSec,
        current: {
          adId: event.info.id,
          filler,
          item,
          startMediaSec: event.mediaSec,
          skipOffsetSec,
          skip: skipOffsetSec === null ? 'none' : 'waiting',
        },
        counts: {
          ads: state.counts.ads + (filler ? 0 : 1),
          fillers: state.counts.fillers + (filler ? 1 : 0),
          unmatched: state.counts.unmatched + (!filler && item === null ? 1 : 0),
        },
      };
    }
    case 'skippable':
      return state.current !== null && state.current.adId === event.info.id && state.current.skip === 'waiting'
        ? { ...state, current: { ...state.current, skip: 'armed' } }
        : state;
    case 'skipExpired':
      return state.current !== null && state.current.adId === event.info.id && state.current.skip !== 'none'
        ? { ...state, current: { ...state.current, skip: 'expired' } }
        : state;
    case 'completed':
      return { ...ONAIR_AD_IDLE, lastSkippedReason: state.lastSkippedReason };
    case 'breakSkipped':
      return { ...ONAIR_AD_IDLE, lastSkippedReason: event.reason };
  }
}

/**
 * **재생 시계** — 재생된 만큼만 는다(`docs/77` §5.5 · CS8 에서 밟았다).
 *
 * ★ `<video>.currentTime` 을 그대로 쓰면 **스킵 뒤에 남은 시간이 엉뚱해진다.** Flower 의 스킵은 hls.js 에 소스를 다시 적재하고
 * 새 재생목록의 시간축(다음 광고의 머리 · 몇 초)으로 착지한다(CS1 S9) — `currentTime` 이 수십 초 뒤로 뛰는데 브레이크는 그대로
 * 흐른다(남은 시간은 Flower 가 다시 묻은 광고로 채운다). 그래서 **두 읽기 사이의 앞으로 간 작은 걸음만** 더하고, 뒤로 가거나
 * 크게 뛴 것은 위치를 옮긴 것으로 보고 버린다. 벽시계가 아니다 — 멈춤·재버퍼링 동안은 늘지 않는다(규칙 57 의 재생 축).
 * 훅이 `timeupdate` 마다 읽어 걸음이 상한을 넘지 않게 한다.
 */
export interface PlayClock {
  accSec: number;
  lastSec: number | null;
}
export const PLAY_CLOCK_START: PlayClock = { accSec: 0, lastSec: null };
/** 한 번의 읽기 사이 이보다 크게 뛰면 재생이 아니라 위치를 옮긴 것이다(`timeupdate` 는 0.25초 · 숨은 탭에서도 1초 간격) */
export const PLAY_CLOCK_MAX_STEP_SEC = 2;

export function advancePlayClock(clock: PlayClock, currentTime: number): PlayClock {
  if (clock.lastSec === null) return { accSec: clock.accSec, lastSec: currentTime };
  const step = currentTime - clock.lastSec;
  return { accSec: clock.accSec + (step > 0 && step <= PLAY_CLOCK_MAX_STEP_SEC ? step : 0), lastSec: currentTime };
}

/**
 * 브레이크의 지난 초 — 광고 시계(`AdCountdown`)가 읽는 축. 브레이크 시작 뒤의 재생 시계(벽시계가 아니다 · 규칙 57 의 축)에, 한가운데
 * 합류면 **합류 전에 지나간 몫**(`breakSec` − Flower 가 알린 남은 길이)을 더한다.
 *
 * 2026-10-28 · 운영 7번 실측 — 시작 뒤 10.3초에 합류한 창의 시계가 1:00 부터 세어 광고가 0:11 을 남기고 끝났다. Flower 의 `onPlay` 에는
 * 인자가 없고 앱은 `CUE-OUT-CONT` 을 읽지 않는다(규칙 101) — 그 몫은 Flower 가 `onAdBreakStarted` 로 알린다(bloom.24).
 */
export function breakElapsedSec(state: OnairAdState, breakSec: number, mediaSec: number): number {
  if (!state.inBreak || state.breakStartMediaSec === null) return 0;
  const before = state.announcedRemainingSec === null ? 0 : Math.max(0, breakSec - state.announcedRemainingSec);
  return before + (mediaSec - state.breakStartMediaSec);
}

/** 브레이크의 남은 초 — `breakSec` 과 `breakElapsedSec` */
export function breakRemainingSec(state: OnairAdState, breakSec: number, mediaSec: number): number | null {
  if (!state.inBreak || state.breakStartMediaSec === null) return null;
  return Math.max(0, Math.ceil(breakSec - breakElapsedSec(state, breakSec, mediaSec)));
}

/** 스킵 버튼 — 개인화 채널의 `SkipButtonState` 와 같은 뜻(보이는가 · 눌리는가 · 남은 초) · `mediaSec` 은 재생 시계 */
export function cspmSkipButton(state: OnairAdState, mediaSec: number): { visible: boolean; enabled: boolean; secondsLeft: number } {
  const ad = state.current;
  if (ad === null || ad.filler || ad.skip === 'none' || ad.skip === 'expired' || ad.skipOffsetSec === null) {
    return { visible: false, enabled: false, secondsLeft: 0 };
  }
  if (ad.skip === 'armed') return { visible: true, enabled: true, secondsLeft: 0 };
  return { visible: true, enabled: false, secondsLeft: Math.max(1, Math.ceil(ad.skipOffsetSec - (mediaSec - ad.startMediaSec))) };
}
